// src/components/MenuForm.jsx
import React, { useState, useEffect } from "react";
import Button1 from "@material-ui/core/Button";

const EMPTY = {
  MENU_CODE:      "",
  MENU_TEXT:      "",
  DESCRIPTION:    "",
  MENU_ACTIVE:    true,
  MENU_LEVEL:     1,
  MENU_PARENT_ID: "",
  MENU_URL:       "",
  MENU_ICON:      "ti-square",
  MENU_SEQ:       1,
};

// Reusable form field label
const Label = ({ children, required }) => (
  <label style={{ fontWeight: 700, fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>
    {children} {required && <span style={{ color: "#dc2626" }}>*</span>}
  </label>
);

const inputStyle = (invalid) => ({
  width:        "100%",
  padding:      "8px 12px",
  borderRadius: 6,
  border:       `1px solid ${invalid ? "#fca5a5" : "#d1d5db"}`,
  fontSize:     13,
  color:        "#1e293b",
  fontFamily:   "inherit",
  outline:      "none",
  boxSizing:    "border-box",
});

export function MenuForm({ open, menu, allMenus, onSave, onClose }) {
  const [form,   setForm]   = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(menu?.MENU_ID);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? {
      MENU_CODE:      menu.MENU_CODE      ?? "",
      MENU_TEXT:      menu.MENU_TEXT      ?? "",
      DESCRIPTION:    menu.DESCRIPTION    ?? "",
      MENU_ACTIVE:    menu.MENU_ACTIVE    ?? true,
      MENU_LEVEL:     menu.MENU_LEVEL     ?? 1,
      MENU_PARENT_ID: menu.MENU_PARENT_ID ?? "",
      MENU_URL:       menu.MENU_URL       ?? "",
      MENU_ICON:      menu.MENU_ICON      ?? "ti-square",
      MENU_SEQ:       menu.MENU_SEQ       ?? 1,
    } : EMPTY);
    setErrors({});
  }, [open, menu]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    const e = {};
    if (!form.MENU_CODE.trim()) e.MENU_CODE = "กรุณากรอก Menu Code";
    if (!form.MENU_TEXT.trim()) e.MENU_TEXT = "กรุณากรอกชื่อ Menu";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        MENU_LEVEL:     Number(form.MENU_LEVEL),
        MENU_SEQ:       Number(form.MENU_SEQ),
        MENU_PARENT_ID: form.MENU_PARENT_ID ? Number(form.MENU_PARENT_ID) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = allMenus.filter(m => m.MENU_ID !== menu?.MENU_ID);

  if (!open) return null;

  return (
    <div style={{
      position:       "fixed",
      inset:          0,
      zIndex:         1300,
      background:     "#0006",
      display:        "flex",
      alignItems:     "center",
      justifyContent: "center",
    }}>
      <div style={{
        background:    "#fff",
        borderRadius:  12,
        width:         "min(580px, 95vw)",
        maxHeight:     "90vh",
        display:       "flex",
        flexDirection: "column",
        boxShadow:     "0 8px 40px #0003",
      }}>
        {/* Header */}
        <div style={{
          padding:       "16px 24px",
          borderBottom:  "1px solid #e2e8f0",
          display:       "flex",
          alignItems:    "center",
          justifyContent: "space-between",
          position:      "sticky",
          top:           0,
          background:    "#fff",
          zIndex:        1,
        }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>
            {isEdit ? "✏️ Edit Menu" : "➕ Add Menu"}
          </span>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20,
            cursor: "pointer", color: "#64748b", lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "24px", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

            {/* Menu Code */}
            <div>
              <Label required>Menu Code</Label>
              <input
                style={inputStyle(errors.MENU_CODE)}
                value={form.MENU_CODE}
                onChange={e => set("MENU_CODE", e.target.value)}
                placeholder="e.g. MNU001"
              />
              {errors.MENU_CODE && (
                <span style={{ fontSize: 11, color: "#dc2626" }}>{errors.MENU_CODE}</span>
              )}
            </div>

            {/* Sequence */}
            <div>
              <Label>Sequence</Label>
              <input
                type="number" min="1"
                style={inputStyle(false)}
                value={form.MENU_SEQ}
                onChange={e => set("MENU_SEQ", e.target.value)}
              />
            </div>

            {/* Menu Name — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label required>Menu Name</Label>
              <input
                style={inputStyle(errors.MENU_TEXT)}
                value={form.MENU_TEXT}
                onChange={e => set("MENU_TEXT", e.target.value)}
                placeholder="ชื่อที่แสดงใน menu"
              />
              {errors.MENU_TEXT && (
                <span style={{ fontSize: 11, color: "#dc2626" }}>{errors.MENU_TEXT}</span>
              )}
            </div>

            {/* Description — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Description</Label>
              <textarea
                style={{ ...inputStyle(false), resize: "vertical", fontFamily: "inherit" }}
                rows={2}
                value={form.DESCRIPTION}
                onChange={e => set("DESCRIPTION", e.target.value)}
                placeholder="คำอธิบายสั้น ๆ"
              />
            </div>

            {/* Level */}
            <div>
              <Label>Level</Label>
              <select
                style={inputStyle(false)}
                value={form.MENU_LEVEL}
                onChange={e => set("MENU_LEVEL", e.target.value)}
              >
                <option value={1}>Level 1 (top)</option>
                <option value={2}>Level 2</option>
                <option value={3}>Level 3</option>
              </select>
            </div>

            {/* Parent */}
            <div>
              <Label>Parent Menu</Label>
              <select
                style={inputStyle(false)}
                value={form.MENU_PARENT_ID}
                onChange={e => set("MENU_PARENT_ID", e.target.value)}
              >
                <option value="">(none — top-level)</option>
                {parentOptions.map(m => (
                  <option key={m.MENU_ID} value={m.MENU_ID}>
                    {"—".repeat(Math.max(0, (m.MENU_LEVEL ?? 1) - 1))} {m.MENU_TEXT}
                  </option>
                ))}
              </select>
            </div>

            {/* URL — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>URL</Label>
              <input
                style={inputStyle(false)}
                value={form.MENU_URL}
                onChange={e => set("MENU_URL", e.target.value)}
                placeholder="/path/to/page"
              />
            </div>

            {/* Icon — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>
                Icon{" "}
                <span style={{ fontWeight: 400, fontSize: 11, color: "#94a3b8" }}>
                  (Tabler icon name เช่น ti-home —{" "}
                  <a href="https://tabler.io/icons" target="_blank" rel="noreferrer"
                    style={{ color: "#6366f1" }}>ดูรายการ</a>)
                </span>
              </Label>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  style={{ ...inputStyle(false), flex: 1 }}
                  value={form.MENU_ICON}
                  onChange={e => set("MENU_ICON", e.target.value)}
                  placeholder="ti-home"
                />
                <i
                  className={`ti ${form.MENU_ICON || "ti-square"}`}
                  style={{ fontSize: 26, color: "#64748b", flexShrink: 0 }}
                />
              </div>
            </div>

            {/* Active toggle — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <Label>Status</Label>
              <label style={{
                display:     "flex",
                alignItems:  "center",
                gap:         10,
                cursor:      "pointer",
                userSelect:  "none",
              }}>
                {/* Track */}
                <div
                  onClick={() => set("MENU_ACTIVE", !form.MENU_ACTIVE)}
                  style={{
                    position:     "relative",
                    width:        38,
                    height:       22,
                    borderRadius: 22,
                    background:   form.MENU_ACTIVE ? "#22c55e" : "#cbd5e1",
                    transition:   "background 0.2s",
                    cursor:       "pointer",
                    flexShrink:   0,
                  }}>
                  {/* Thumb */}
                  <div style={{
                    position:     "absolute",
                    top:          3,
                    left:         form.MENU_ACTIVE ? 18 : 3,
                    width:        16,
                    height:       16,
                    borderRadius: "50%",
                    background:   "#fff",
                    transition:   "left 0.2s",
                  }} />
                </div>
                <span style={{ fontSize: 13, color: form.MENU_ACTIVE ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
                  {form.MENU_ACTIVE ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
          </div>

          {/* Note */}
          {!isEdit && (
            <div style={{
              marginTop:    16,
              background:   "#fffbeb",
              border:       "1px solid #fde68a",
              borderRadius: 6,
              padding:      "10px 14px",
              fontSize:     12,
              color:        "#92400e",
            }}>
              💡 หลังบันทึก menu จะปรากฏใน tree ด้านซ้าย สามารถ drag หรือแก้ไข sequence เพื่อเรียงลำดับใหม่ได้
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:       "14px 24px",
          borderTop:     "1px solid #e2e8f0",
          display:       "flex",
          justifyContent: "flex-end",
          gap:           10,
          background:    "#fff",
        }}>
          <Button1
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            style={{ textTransform: "none", fontSize: 13 }}
          >
            ยกเลิก
          </Button1>
          <Button1
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={saving}
            style={{ textTransform: "none", fontSize: 13, fontWeight: 700 }}
          >
            {saving ? "⏳ กำลังบันทึก..." : "💾 บันทึก"}
          </Button1>
        </div>
      </div>
    </div>
  );
}
